// VisitorsPanel — front-office visitor register: sign in, sign out, by day.
import { useEffect, useState } from 'react';
import { LogOut, Trash2, Download } from 'lucide-react';
import { getVisitors, addVisitor, checkoutVisitor, deleteVisitor } from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';
import { Field, Msg, errText, inputCls, theadRowCls, EmptyRow } from '../ui/Form';
import { fmtDate, fmtTime, todayISO, downloadCsv } from '../ui/format';

const EMPTY = { name: '', phone: '', purpose: '', whom_to_meet: '', persons: 1, notes: '' };
const PURPOSES = ['Admission enquiry', 'Meet teacher', 'Fee payment', 'Pick up child', 'Delivery', 'Official visit'];

export default function VisitorsPanel() {
  const [date, setDate] = useState(todayISO);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [msg,  setMsg]  = useState('');

  useEffect(() => { load(); }, [date]);
  async function load() {
    try { setRows(await getVisitors(date === todayISO() ? undefined : date)); } catch (err) { setMsg(errText(err)); }
  }

  async function add(e) {
    e.preventDefault(); setMsg('');
    try { await addVisitor({ ...form, persons: Number(form.persons) || 1 }); setForm(EMPTY); setMsg('Visitor signed in ✅'); setDate(todayISO()); load(); }
    catch (err) { setMsg(errText(err)); }
  }
  async function out(v) { try { await checkoutVisitor(v.id); load(); } catch (err) { setMsg(errText(err)); } }
  async function remove(v) {
    if (!window.confirm(`Delete the entry for ${v.name}?`)) return;
    try { await deleteVisitor(v.id); load(); } catch (err) { setMsg(errText(err)); }
  }

  const inside = rows.filter(v => !v.out_time).length;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h2 className="font-display font-extrabold text-xl text-ink">Visitor Register</h2>
        <input type="date" className={inputCls} value={date} max={todayISO()} onChange={e => setDate(e.target.value || todayISO())} aria-label="Date" />
      </div>
      <Msg msg={msg} className="mb-3" />

      <div className="grid grid-cols-[minmax(280px,360px)_1fr] gap-5 items-start max-[1000px]:grid-cols-1">
        <Card className="p-5">
          <h3 className="font-display font-bold text-[15px] text-ink mb-3">Sign in a visitor</h3>
          <form onSubmit={add} className="flex flex-col gap-3">
            <Field label="Name"><input required className={inputCls} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Phone"><input type="tel" className={inputCls} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Purpose">
              <input required list="visit-purposes" className={inputCls} value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} />
              <datalist id="visit-purposes">{PURPOSES.map(p => <option key={p} value={p} />)}</datalist>
            </Field>
            <div className="grid grid-cols-[1fr_80px] gap-3">
              <Field label="Whom to meet"><input className={inputCls} value={form.whom_to_meet} onChange={e => setForm({ ...form, whom_to_meet: e.target.value })} /></Field>
              <Field label="People"><input type="number" min="1" className={inputCls} value={form.persons} onChange={e => setForm({ ...form, persons: e.target.value })} /></Field>
            </div>
            <Field label="Notes"><input className={inputCls} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
            <Button type="submit" className="w-fit">Sign in</Button>
          </form>
        </Card>

        <Card>
          <CardHeader title={`${date === todayISO() ? 'Today' : fmtDate(date)} — ${rows.length} visitor${rows.length === 1 ? '' : 's'}${inside ? ` · ${inside} still inside` : ''}`}
            action={<Button variant="secondary" className="!py-1.5 !text-xs flex items-center gap-1.5" disabled={!rows.length}
              onClick={() => downloadCsv(`visitors-${date}.csv`, [
                { key: 'name', label: 'Name' }, { key: 'phone', label: 'Phone' }, { key: 'purpose', label: 'Purpose' },
                { key: 'whom_to_meet', label: 'Met' }, { key: 'persons', label: 'People' }, { key: 'in', label: 'In' }, { key: 'out', label: 'Out' },
              ], rows.map(v => ({ ...v, in: `${fmtDate(v.in_time)} ${fmtTime(v.in_time)}`, out: v.out_time ? fmtTime(v.out_time) : '' })))}>
              <Download size={13} /> CSV</Button>} />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className={theadRowCls}><th className="px-4 py-2.5">Visitor</th><th className="px-4 py-2.5">Purpose</th><th className="px-4 py-2.5">In</th><th className="px-4 py-2.5">Out</th><th className="px-4 py-2.5"></th></tr></thead>
              <tbody>
                {rows.map(v => (
                  <tr key={v.id} className="border-t border-line-soft align-top">
                    <td className="px-4 py-3"><b className="text-ink">{v.name}</b>{v.persons > 1 ? <span className="text-ink-soft"> +{v.persons - 1}</span> : null}
                      {v.phone && <span className="block text-xs text-ink-soft">{v.phone}</span>}</td>
                    <td className="px-4 py-3">{v.purpose}{v.whom_to_meet && <span className="block text-xs text-ink-soft">Met {v.whom_to_meet}</span>}{v.notes && <span className="block text-xs text-ink-soft">{v.notes}</span>}</td>
                    <td className="px-4 py-3 whitespace-nowrap tabular-nums">{fmtTime(v.in_time)}{v.in_time.slice(0, 10) !== date && <span className="block text-xs text-ink-soft">{fmtDate(v.in_time)}</span>}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {v.out_time ? <span className="tabular-nums">{fmtTime(v.out_time)}</span>
                        : <Button variant="secondary" className="!px-2.5 !py-1 !text-xs flex items-center gap-1" onClick={() => out(v)}><LogOut size={12} /> Sign out</Button>}
                    </td>
                    <td className="px-4 py-3"><button onClick={() => remove(v)} aria-label="Delete entry" className="text-ink-soft hover:text-critical p-1"><Trash2 size={14} /></button></td>
                  </tr>
                ))}
                {!rows.length && <EmptyRow cols={5}>No visitors</EmptyRow>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
